import React, { use } from 'react';
import { ControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CommentOutlined from '@mui/icons-material/CommentOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ViewingModeController from 'shared/components/viewing-mode-controller/viewing-mode-controller';

import useResidentSettingDetailsInformationAccordion from 'features/residents/resident-settings/components/resident-setting-details-information-accordion/hooks';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import styles from './resident-setting-details-information-accordion.module.scss';

const ResidentSettingDetailsInformationAccordion = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const {
    detailEditState: { viewingMode },
  } = use(ResidentSettingsProviderContext);

  const { shouldShowInformation, shouldShowInformationNote, toggleShouldShowInformation } = useResidentSettingDetailsInformationAccordion();

  const renderViewingModeText: ControllerProps['render'] = ({ field }) => (
    <TextField
      {...field}
      slotProps={{ htmlInput: { maxLength: 255 } }}
      id="noteInput"
      variant="outlined"
      multiline
      minRows={2}
      maxRows={4}
      className="form-field"
      value={`${field.value ?? ''}`}
    />
  );

  return (
    <Accordion expanded={shouldShowInformation} disableGutters>
      <AccordionSummary
        sx={{ padding: 0, margin: '12px 0 0 0', '.MuiAccordionSummary-content': { margin: '0' } }}
        expandIcon={<ExpandMore onClick={toggleShouldShowInformation} />}
        aria-label="Expand"
        aria-controls="additional-actions1-content"
      >
        <Grid
          className={styles.accordionSummaryContent}
          onClick={toggleShouldShowInformation}
          onKeyDown={(e) => e.key === 'Enter' && toggleShouldShowInformation}
        >
          <CommentOutlined onClick={toggleShouldShowInformation} />
          <Typography variant="h5">{t('details.information')}</Typography>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '0 0 8px 0', margin: 0 }}>
        <div className={styles.information}>
          <div className={styles.noteContainer}>
            <Typography variant="subtitle1">{t('details.informationNote')}</Typography>
          </div>
          {shouldShowInformationNote ? (
            <ViewingModeController viewingMode={viewingMode} name="note" render={renderViewingModeText} />
          ) : (
            <Typography variant="subtitle1">{t('details.noInformationNote')}</Typography>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default ResidentSettingDetailsInformationAccordion;
